import uniq from "lodash/uniq";
import { useMemo, useEffect, useCallback } from "react";

import { paths } from "src/routes/paths";
import { useRouter } from "src/routes/hooks";

import { getStorage, useLocalStorage } from "src/hooks/use-local-storage";

import { PRODUCT_CHECKOUT_STEPS } from "src/_mock/_product";

import { IAddressItem } from "src/types/address";
import { ICheckoutItem } from "src/types/checkout";

import { CheckoutContext } from "./checkout-context";
import { IVenue } from "src/types/venue";
import { t } from "i18next";

// ----------------------------------------------------------------------

const STORAGE_KEY = "checkout";

// const initialState = {
//   activeStep: 0,
//   items: [],
//   subTotal: 0,
//   total: 0,
//   discount: 0,
//   shipping: 0,
//   billing: null,
//   totalItems: 0,
// };

const initialState = {
  activeStep: 0,
  items: [],
  subTotal: 0,
  total: 0,
  discount: 0,
  shipping: 0,
  billing: null,
  totalItems: 0,
  type: "",
  creditTotal: 0, // Add credit total
  currency: "",
  id: 0,
  paymentMethods: [],
  provider_urltag: "",
  provider_name: "",
  comment: "",
};

type Props = {
  children: React.ReactNode;
};

export function CheckoutProvider({ children }: Props) {
  const router = useRouter();

  const { state, update, reset } = useLocalStorage(STORAGE_KEY, initialState);

  const onGetCart = useCallback(() => {
    const totalItems: number = state.items.reduce(
      (total: number, item: ICheckoutItem) => total + item.quantity,
      0
    );

    const subTotal: number = state.items.reduce(
      (total: number, item: ICheckoutItem) =>
        total + item.quantity * item.price,
      0
    );

    const creditTotal: number = state.items.reduce(
      (total: number, item: ICheckoutItem) =>
        total + (item.credit || 0) * item.quantity,
      0
    );

    update("id", state.id);
    update("provider_urltag", state.provider_urltag);
    update("subTotal", subTotal);
    update("type", state.type);
    update("currency", state.currency);
    update("totalItems", totalItems);
    update("creditTotal", creditTotal); // Update creditTotal
    update("billing", state.activeStep === 1 ? null : state.billing);
    update("discount", state.items.length ? state.discount : 0);
    update("shipping", state.items.length ? state.shipping : 0);
    update("total", state.subTotal - state.discount + state.shipping);
    update("paymentMethods", state.paymentMethods);
    update("specialPaymentSettings", state.specialPaymentSettings);
    update("comment", state.comment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.items,
    state.activeStep,
    state.billing,
    state.discount,
    state.shipping,
    state.subTotal,
    state.type,
    state.currency,
    state.id,
    state.provider_urltag,
    state.comment,
  ]);

  useEffect(() => {
    const restored = getStorage(STORAGE_KEY);

    if (restored) {
      onGetCart();
    }
  }, [onGetCart]);

  const loadTransaction = useCallback(
    (transaction: any) => {
      if (!transaction || !transaction.transactionitems) return;

      const items: ICheckoutItem[] = transaction.transactionitems.map(
        (item: any) => {
          const baseItem = {
            id: item.id,
            type: item.item_type,
            price: parseFloat(item.price),
            credit: parseFloat(item.credit),
            quantity: 1, // Default to 1 unless otherwise specified
            currency: transaction.transaction.currency,
          };

          if (
            item.item_type === "training" ||
            item.item_type === "reservation"
          ) {
            return {
              ...baseItem,
              startDate: item.reservation?.start_date,
              endDate: item.reservation?.end_date,
              venueId: item.reservation?.venue_id,
              venueName: transaction.providerData.venues.find(
                (venue: IVenue) => item.reservation?.venue_id === venue.id
              )?.name,
              name: item.reservation?.event_name,
            };
          }

          if (item.item_type === "pass") {
            return {
              ...baseItem,
              name: item.userpass?.name || t("pass"),
              credit: 0,
              startDate: item.userpass?.period_start,
              endDate: item.userpass?.period_end,
            };
          }

          if (item.item_type === "ticket") {
            return {
              ...baseItem,
              name: item.userticket?.name || t("ticket"),
              // startDate: item.userticket?.event_start,
              // endDate: item.userticket?.event_end,
            };
          }

          if (item.item_type === "tournamentapplication") {
            return {
              ...baseItem,
              name: item?.tournament_name + " - " + item?.category_name,
            };
          }

          return baseItem;
        }
      );

      const subTotal = items.reduce((sum, item) => sum + item.price, 0);
      const creditTotal = items.reduce((sum, item) => sum + item.credit, 0);
      const total = subTotal; // Adjust for discounts, shipping if needed

      update("id", transaction.transaction.id);
      update("provider_urltag", transaction.transaction.provider_urltag);
      update("provider_name", transaction.providerData.provider_name);
      update("items", items);
      update("type", transaction.transaction.type);
      update("currency", transaction.transaction.currency);
      update("subTotal", subTotal);
      update("creditTotal", creditTotal);
      update("total", total);
      update("totalItems", items.length);
      update("paymentMethods", transaction.transaction.payment_methods);
      update(
        "specialPaymentSettings",
        transaction.transaction.special_payment_settings
      );
      update("activeStep", 0);
      update("comment", "");
    },
    [update]
  );

  const onAddToCart = useCallback(
    (newItem: ICheckoutItem) => {
      const updatedItems: ICheckoutItem[] = state.items.map(
        (item: ICheckoutItem) => {
          if (item.id === newItem.id) {
            return {
              ...item,
              colors: uniq([...item.colors, ...newItem.colors]),
              quantity: item.quantity + 1,
            };
          }
          return item;
        }
      );

      if (!updatedItems.some((item: ICheckoutItem) => item.id === newItem.id)) {
        updatedItems.push(newItem);
      }

      update("items", updatedItems);
    },
    [update, state.items]
  );

  const onDeleteCart = useCallback(
    (itemId: string) => {
      const updatedItems = state.items.filter(
        (item: ICheckoutItem) => item.id !== itemId
      );

      update("items", updatedItems);
    },
    [update, state.items]
  );

  const onBackStep = useCallback(() => {
    update("activeStep", state.activeStep - 1);
  }, [update, state.activeStep]);

  const onNextStep = useCallback(() => {
    update("activeStep", state.activeStep + 1);
  }, [update, state.activeStep]);

  const onGotoStep = useCallback(
    (step: number) => {
      update("activeStep", step);
    },
    [update]
  );

  const onIncreaseQuantity = useCallback(
    (itemId: string) => {
      const updatedItems = state.items.map((item: ICheckoutItem) => {
        if (item.id === itemId) {
          return {
            ...item,
            quantity: item.quantity + 1,
          };
        }
        return item;
      });

      update("items", updatedItems);
    },
    [update, state.items]
  );

  const onDecreaseQuantity = useCallback(
    (itemId: string) => {
      const updatedItems = state.items.map((item: ICheckoutItem) => {
        if (item.id === itemId) {
          return {
            ...item,
            quantity: item.quantity - 1,
          };
        }
        return item;
      });

      update("items", updatedItems);
    },
    [update, state.items]
  );

  const onCreateBilling = useCallback(
    (address: IAddressItem) => {
      update("billing", address);

      onNextStep();
    },
    [onNextStep, update]
  );

  const onApplyComment = useCallback(
    (comment: string) => {
      update("comment", comment);
    },
    [update]
  );

  const onApplyDiscount = useCallback(
    (discount: number) => {
      update("discount", discount);
    },
    [update]
  );

  const onApplyShipping = useCallback(
    (shipping: number) => {
      update("shipping", shipping);
    },
    [update]
  );

  const completed = state.activeStep === PRODUCT_CHECKOUT_STEPS.length;

  const onComplete = useCallback(() => {
    if (completed) {
    }
  }, [completed]);
  // Reset
  const onReset = useCallback(() => {
    if (completed) {
      reset();
      router.replace(paths.sportproviders);
    }
  }, [completed, reset, router]);

  const memoizedValue = useMemo(
    () => ({
      ...state,
      completed,
      loadTransaction, // Expose the function
      //
      onAddToCart,
      onDeleteCart,
      //
      onIncreaseQuantity,
      onDecreaseQuantity,
      //
      onCreateBilling,
      onApplyDiscount,
      onApplyShipping,
      onApplyComment,
      //
      onBackStep,
      onNextStep,
      onGotoStep,
      //
      onReset,
      onComplete,
    }),
    [
      completed,
      loadTransaction,
      onAddToCart,
      onApplyDiscount,
      onApplyShipping,
      onApplyComment,
      onBackStep,
      onCreateBilling,
      onDecreaseQuantity,
      onDeleteCart,
      onGotoStep,
      onIncreaseQuantity,
      onNextStep,
      onReset,
      onComplete,
      state,
    ]
  );

  return (
    <CheckoutContext.Provider value={memoizedValue}>
      {children}
    </CheckoutContext.Provider>
  );
}
